<script setup lang="ts">
import DateSpecificHours from '@/assets/date-specific-hours.png'
import {
  Pencil01Icon,
  PlusIcon,
  Trash01Icon,
  XCloseIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIButton,
  UIContentWrap,
  UIDatepicker,
  UIModal,
  UIModalContent,
  UIModalFooter,
  UITimePicker,
  UITooltip,
} from '@gohighlevel/ghl-ui'
import moment from 'moment'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { GHLLanguageKeysMapper } from '@/utils/constants'
import AppState from '../../states/app'
import { DayAvailability } from './DayAvailability'
import { Schedule } from './Schedule'

moment.locale(GHLLanguageKeysMapper[AppState.locale])

const showOverRideModal = ref(false)
const activeId = ref('')
const listContainerBoxRef = ref<HTMLElement | null>(null)
const itemRefs = ref<HTMLElement[]>([])
const visibleItems = ref<DayAvailability[]>([])
const showingAll = ref(false)
const containerHeight = ref(680)
let resizeObserver = null as ResizeObserver | null
const overriddenDates = ref(
  moment().tz(AppState.locationTimezone).format('MMMM D, YYYY')
)

const props = defineProps({
  schedule: Schedule,
  isRecurringCalendarSelected: Boolean,
})

const editMode = ref(false)
const overriddenHours = ref([
  {
    startTime: '8:0',
    endTime: '17:0',
  },
])
const customSlotSelectorModal = ref()

const allItems = computed(() => methods.loadData())
const remainingItemsCount = computed(
  () => allItems.value.length - visibleItems.value.length
)

const onClickAddSpecificHours = () => {
  if (overlappingTimes.value.length > 0 || !props.schedule) {
    return
  }

  const newRule = new DayAvailability({
    type: 'date',
    date: moment(overriddenDates.value, 'MMMM D, YYYY').format('YYYY-MM-DD'),
    intervals: overriddenHours.value.map(x => ({
      from: x.startTime,
      to: x.endTime,
    })),
    isActive: true,
  })

  const existingRuleIndex = props.schedule.rules.findIndex(
    rule => rule.type === 'date' && rule.date === newRule.date
  )

  let updatedRules
  if (existingRuleIndex !== -1) {
    // Update the existing rule
    updatedRules = [
      ...props.schedule.rules.slice(0, existingRuleIndex),
      newRule,
      ...props.schedule.rules.slice(existingRuleIndex + 1),
    ]
  } else {
    // Add new rule
    updatedRules = [newRule, ...props.schedule.rules]
  }
  // Update the schedule with the new rules
  props.schedule.setRules(updatedRules)

  showOverRideModal.value = false
  activeId.value = ''
  editMode.value = false
}

const cancelCallback = () => {
  showOverRideModal.value = false
  overriddenDates.value = moment()
    .tz(AppState.locationTimezone)
    .format('MMMM D, YYYY')
  overriddenHours.value = [
    {
      startTime: '8:0',
      endTime: '17:0',
    },
  ]
  activeId.value = ''
  editMode.value = false
}

const methods = {
  openOverrideModal() {
    showOverRideModal.value = true
    editMode.value = false
    overriddenHours.value = [
      {
        startTime: '8:0',
        endTime: '17:0',
      },
    ]
    overriddenDates.value = moment()
      .tz(AppState.locationTimezone)
      .format('MMMM D, YYYY')
  },
  loadData() {
    return props.schedule?.getDateSpecificHours() || []
  },
  async addNextSlots(overriddenHours, item) {
    overriddenHours.push({
      startTime: moment(item.endTime, 'H:m')
        .set({
          hour: moment(item.endTime, 'H:m').hour() + 1,
        })
        .format('H:m'),
      endTime: moment(item.endTime, 'H:m')
        .set({
          hour: moment(item.endTime, 'H:m').hour() + 2,
        })
        .format('H:m'),
    })
    await nextTick()
    customSlotSelectorModal.value.scrollTop =
      customSlotSelectorModal.value.scrollHeight
  },
  async removeHours(index) {
    overriddenHours.value.length > 0
      ? overriddenHours.value.splice(index, 1)
      : (overriddenHours.value = [])
  },

  dateDisabled(ts: number) {
    return props.schedule?.isDateDisabled(ts) || false
  },
}

const itemsOverflowContainer = computed(() => {
  if (!itemRefs.value.length || !listContainerBoxRef.value) {
    return false
  }
  const totalItemsHeight = itemRefs.value.reduce(
    (sum, el) => sum + (el?.offsetHeight || 0),
    0
  )
  return totalItemsHeight > listContainerBoxRef.value.clientHeight
})

const invalidTimes = computed(() => {
  return (
    overriddenHours.value.filter(x => {
      if (
        (x.startTime === '0:0' && x.endTime === '0:0') ||
        x.endTime === '0:0'
      ) {
        return false
      }
      return (
        moment(x.startTime, 'H:m').isSameOrAfter(moment(x.endTime, 'H:m')) ||
        moment(x.endTime, 'H:m').isSameOrBefore(moment(x.startTime, 'H:m'))
      )
    }).length > 0
  )
})

const has24hourError = computed(() => {
  overriddenHours.value.forEach(h => {
    if (
      h.startTime === '0:0' &&
      h.endTime === '0:0' &&
      overriddenHours.value.length > 1
    ) {
      return true
    }
  })
  return false
})

const is24HourSelected = computed(() => {
  return overriddenHours.value.length === 1
    ? overriddenHours.value[0].startTime === '0:0' &&
    overriddenHours.value[0].endTime === '0:0'
    : false
})

const overlappingTimes = computed(() => {
  const overlaps = [] as number[]

  if (
    overriddenHours.value.length === 1 &&
    overriddenHours.value[0].startTime === '0:0' &&
    overriddenHours.value[0].endTime === '0:0'
  ) {
    return []
  }

  for (let i = 0; i < overriddenHours.value.length - 1; i++) {
    for (let j = i + 1; j < overriddenHours.value.length; j++) {
      const startTime1 = moment(overriddenHours.value[i].startTime, 'H:mm')
      const endTime1 = moment(overriddenHours.value[i].endTime, 'H:mm')
      const startTime2 = moment(overriddenHours.value[j].startTime, 'H:mm')
      const endTime2 = moment(overriddenHours.value[j].endTime, 'H:mm')

      if (
        startTime1.isBetween(startTime2, endTime2) ||
        endTime1.isBetween(startTime2, endTime2) ||
        startTime2.isBetween(startTime1, endTime1) ||
        endTime2.isBetween(startTime1, endTime1)
      ) {
        overlaps.push(i)
        overlaps.push(j)
      }
    }
  }

  return [...new Set(overlaps)]
})

const handleEdit = (rule: DayAvailability) => {
  activeId.value = rule.date || ''
  editMode.value = true
  overriddenDates.value = moment(rule.date).format('MMMM D, YYYY')
  overriddenHours.value = rule.intervals.map(x => ({
    startTime: x.from,
    endTime: x.to,
  }))
  showOverRideModal.value = true
}

const showAllItems = () => {
  showingAll.value = true
  visibleItems.value = [...allItems.value]
}
const showLessItems = () => {
  showingAll.value = false
  calculateVisibleItems()
}
const handleResize = () => {
  if (!showingAll.value) {
    calculateVisibleItems()
  }
}

watch(
  () => props.schedule,
  () => {
    if (!showingAll.value) {
      calculateVisibleItems()
    } else {
      visibleItems.value = [...allItems.value]
    }
  },
  { deep: true }
)
const calculateVisibleItems = () => {
  visibleItems.value = []
  let currentHeight = 0
  for (let i = 0; i < allItems.value.length; i++) {
    visibleItems.value.push(allItems.value[i])
    nextTick(() => {
      if (itemRefs.value && itemRefs.value.length > 0) {
        const lastItemHeight = itemRefs.value[i].offsetHeight
        currentHeight += lastItemHeight

        if (currentHeight > containerHeight.value) {
          visibleItems.value.pop()
          return
        }
        if (
          i < allItems.value.length - 1 &&
          currentHeight + 40 > containerHeight.value
        ) {
          visibleItems.value.pop()
        }
      }
    })
  }
}

onMounted(() => {
  nextTick(() => {
    calculateVisibleItems()

    resizeObserver = new ResizeObserver(() => {
      if (!showingAll.value) {
        calculateVisibleItems()
      }
    })

    if (listContainerBoxRef.value) {
      resizeObserver.observe(listContainerBoxRef.value)
    }

    window.addEventListener('resize', handleResize)
  })
})

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
  window.removeEventListener('resize', handleResize)
})

defineExpose({
  methods,
})
</script>
<template>
  <UIContentWrap :locale="GHLLanguageKeysMapper[AppState.locale]" class="!p-0">
    <div class="mb-4 flex flex-col gap-1 text-sm">
      <h4 class="text-sm font-semibold">
        {{ $t('calendar_advanced.availability.specific_hours_button') }}
      </h4>
      <p class="text-sm text-gray-500">
        {{ $t('calendar_advanced.availability.specific_hours_description') }}
      </p>
    </div>
    <div class="flex">
      <div v-if="props.isRecurringCalendarSelected">
        <UITooltip placement="top">
          <template #trigger>
            <UIButton type="default" :disabled="props.isRecurringCalendarSelected" @click="methods.openOverrideModal">
              <PlusIcon class="mr-2 h-4 w-4" />
              {{ $t('calendar_advanced.availability.date_specific_level') }}
            </UIButton>
          </template>
          <p class="max-w-sm">
            {{
              $t(
                'settings.availability.schedule.date_specific_hours_recurring_meetings_enabled'
              )
            }}
          </p>
        </UITooltip>
      </div>

      <UIButton v-else type="default" :disabled="props.isRecurringCalendarSelected" @click="methods.openOverrideModal">
        <PlusIcon class="mr-2 h-4 w-4" />
        {{ $t('calendar_advanced.availability.date_specific_level') }}
      </UIButton>
    </div>
    <!-- empty state -->
    <div v-if="methods.loadData().length === 0" class="my-20 flex flex-col items-center justify-center">
      <img :src="DateSpecificHours" class="mb-2 h-36" alt="date-specific-hours" />
      <p class="text-md text-center font-semibold text-gray-900">
        {{ $t('calendar_advanced.availability.no_date_specific_heading') }}
      </p>

      <p class="text-center text-xs text-gray-600">
        {{ $t('calendar_advanced.availability.no_date_specific_description') }}
      </p>
    </div>

    <!-- list of date specific hours -->
    <div v-else ref="listContainerBoxRef" class="h-[680px] min-h-[560px]">
      <div v-for="(rule, index) in visibleItems" :key="index" ref="itemRefs"
        class="mt-4 flex cursor-pointer items-start justify-between whitespace-pre-wrap border-b border-gray-200 px-2 py-4"
        :class="{
          'line-through': methods.dateDisabled(moment(rule.date).valueOf()),
        }" role="button">
        <div class="flex-1">
          <p class="text-sm font-semibold text-gray-700">
            {{ moment(rule.date).format('MMMM D, YYYY') }}
          </p>
          <p v-if="rule.intervals.length === 0" class="text-sm text-gray-500">
            {{ $t('calendar_advanced.availability.unavailable_label') }}
          </p>
          <p v-for="(interval, intervalIndex) in rule.intervals" v-else :key="intervalIndex"
            class="mt-1 text-sm font-normal text-gray-500">
            {{ moment(interval.from, 'HH:mm').format('hh:mm A') }} -
            {{ moment(interval.to, 'HH:mm').format('hh:mm A') }}
          </p>
        </div>
        <UIButton id="edit-availability-hour-custom" class="px-3 text-gray-600" :text="true"
          :aria-label="$t('aria_labels.edit_custom_slot')" @click.stop="handleEdit(rule)">
          <Pencil01Icon class="w-4" />
        </UIButton>
        <UIButton id="remove-availability-hour-custom" class="px-3 text-gray-700" :text="true"
          :aria-label="$t('aria_labels.remove_custom_slot')"
          @click.stop="props.schedule?.removeDateSpecificHours(index)">
          <Trash01Icon class="w-4" />
        </UIButton>
      </div>

      <!-- Show 'n more' button if there are hidden items and not expanded -->
      <UIButton v-if="!showingAll && remainingItemsCount > 0" class="mt-2 w-full" text="true" @click="showAllItems">
        {{ remainingItemsCount }}
        {{
          $t('settings.availability.schedule.date_specific_hours_show_more') ||
          'more'
        }}
      </UIButton>

      <!-- Show 'Show less' button if expanded -->
      <UIButton v-else-if="showingAll && itemsOverflowContainer" class="mt-2 w-full" text="true" @click="showLessItems">
        {{
          $t('settings.availability.schedule.date_specific_hours_show_less') ||
          'Show less'
        }}
      </UIButton>
    </div>
    <UIModal :show="showOverRideModal" :width="460">
      <template #header>
        <div class="flex w-full items-center justify-between">
          <p class="w-3/4 text-lg text-gray-900">
            {{ $t('calendar_advanced.availability.subTitle') }}
            <span class="hidden">{{ overriddenDates }}</span>
          </p>
          <XCloseIcon class="h-6 w-6 cursor-pointer text-gray-500" :aria-label="$t('aria_labels.close_modal')"
            role="button" @click="cancelCallback()" @keydown="cancelCallback()" />
        </div>
      </template>
      <UIModalContent>
        <div id="do-picker" class="mb-5 flex items-center justify-center rounded-lg border-[1px] border-gray-200 py-8">
          <UIDatepicker id="date-override picker" v-model:formatted-value="overriddenDates" :actions="[]" :panel="true"
            class="scale-125 transform" :is-date-disabled="methods.dateDisabled" format="MMMM d, yyyy" />
        </div>
        <div class="border-[1px] border-gray-200 p-4">
          <div class="flex items-center justify-between">
            <p class="mb-3 text-sm font-semibold text-gray-800">
              {{ $t('calendar_advanced.availability.heading_2') }}
            </p>
            <UIButton v-if="overriddenHours.length === 0" id="add-override-custom"
              :aria-label="$t('aria_labels.add_custom_slot')" :text="true" class="text-gray-700" @click="
                overriddenHours.push({
                  startTime: '8:0',
                  endTime: '17:0',
                })
                ">
              <PlusIcon class="w-4" />
            </UIButton>
          </div>

          <div v-if="overriddenDates" ref="customSlotSelectorModal" class="max-h-40 overflow-y-auto">
            <div v-if="overriddenHours.length == 0">
              <p class="text-md font-semibold text-gray-600">
                {{ $t('calendar_advanced.availability.unavailable_label') }}
              </p>
            </div>
            <div v-for="(item, index) in overriddenHours" :key="index" class="mb-2 flex items-center gap-2">
              <UITimePicker v-model:formatted-value="item.startTime" value-format="H:m" :minutes="5" format="hh:mm a"
                use-12-hours :actions="['confirm']" class="w-full" :status="!item.startTime ||
                    overlappingTimes.includes(index) ||
                    (moment(item.endTime, 'H:m').isSameOrBefore(
                      moment(item.startTime, 'H:m')
                    ) &&
                      item.endTime !== '0:0' &&
                      item.startTime !== '0:0')
                    ? 'error'
                    : 'success'
                  " />

              <div class="w-8 min-w-max font-semibold capitalize">
                {{ $t('calendar_advanced.availability.to') }}
              </div>
              <UITimePicker v-model:formatted-value="item.endTime" value-format="H:m" :minutes="5" format="hh:mm a"
                use-12-hours :actions="['confirm']" class="w-full" :status="!item.endTime ||
                    overlappingTimes.includes(index) ||
                    (moment(item.endTime, 'H:m').isSameOrBefore(
                      moment(item.startTime, 'H:m')
                    ) &&
                      item.endTime !== '0:0' &&
                      item.startTime !== '0:0')
                    ? 'error'
                    : 'success'
                  " />
              <div class="mr-6 flex items-center justify-around">
                <div class="ml-4 w-4">
                  <UIButton v-if="index === overriddenHours.length - 1" id="" :text="true" class="text-gray-700"
                    :disabled="is24HourSelected" :aria-label="$t('aria_labels.add_custom_slot')"
                    @click="methods.addNextSlots(overriddenHours, item)">
                    <PlusIcon class="w-4" />
                  </UIButton>
                </div>
                <div class="ml-4 w-4">
                  <UIButton id="add-override-hour-custom" class="text-gray-700" :text="true"
                    :aria-label="$t('aria_labels.remove_custom_slot')" @click="methods.removeHours(index)">
                    <Trash01Icon class="w-4" />
                  </UIButton>
                </div>
              </div>
            </div>
          </div>
          <p v-if="overlappingTimes.length > 0" class="text-left text-sm text-red-600">
            {{ $t('calendar_advanced.availability.overlap_error') }}
          </p>
        </div>
      </UIModalContent>
      <template #footer>
        <UIModalFooter id="modal-footer" positive-text="Submit" negative-text="Cancel" :disabled="overlappingTimes.length > 0 || invalidTimes || has24hourError
          " @positive-click="onClickAddSpecificHours" @negative-click="cancelCallback" />
      </template>
    </UIModal>
  </UIContentWrap>
</template>
<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
<style lang="scss">
#do-picker {
  --primary-600: #344054 !important;

  .n-date-panel-weekdays {
    font-weight: 500 !important;
  }

  .n-date-panel-month__prev {
    margin-left: -50px;
    transform: scale(1.5);
  }

  .n-date-panel-month__next {
    margin-right: -50px;
    transform: scale(1.5);
  }

  .n-date-panel-month__fast-next {
    height: 0px !important;
    width: 0px !important;
  }

  .n-date-panel-month__fast-prev {
    height: 0px !important;
    width: 0px !important;
  }

  .n-tabs {
    height: 90%;
    overflow: hidden;
  }

  .n-tab-pane {
    height: 90% !important;
    overflow: scroll !important;
  }
}
</style>
